import {TranslationObject} from '@colonist/i18n'
import {ViewPopup} from './ViewPopup'
import {createDivIn, createImgIn, createHeaderIn, createParagraphIn, setElementOnClick} from '../ui_utils/DomUtils'
import {UIProfileEditAvatarController} from '../ui_html/header/UIProfileEditAvatarController'
import {ClientUserState} from '../ClientUserState'
import {LobbyImage} from '@colonist/types'
import {PlayerColor} from '@colonist/game-states'
import {UIPlayerColorController} from '../UIPlayerColorController'
import {UIProfileUsernameController} from '../ui_html/header/UIProfileUsernameController'

export enum EditProfileTab {
    Avatar,
    Color,
}

export interface EditProfileData {
    usernameController: new (title?: TranslationObject, body?: TranslationObject, xAction?: () => void) => UIProfileUsernameController
    selectColorAction: (color: PlayerColor) => Promise<void>
    tab?: EditProfileTab
    selectedColor?: PlayerColor | undefined
    username?: string
}

export class ViewPopupEditProfile extends ViewPopup {
    private tab: EditProfileTab = EditProfileTab.Avatar
    private selectedColor?: PlayerColor | undefined

    protected toggleContainer: HTMLDivElement
    private toggleButtonAvatar: HTMLDivElement
    private toggleButtonColor: HTMLDivElement

    private avatarSelectorContainer: HTMLDivElement
    protected colorSelectorContainer: HTMLDivElement

    private readonly editUsernameAction: () => void
    private readonly selectColorAction: (color: PlayerColor) => Promise<void>

    constructor(editProfileData: EditProfileData, popupTitle?: TranslationObject) {
        const title: TranslationObject = popupTitle ?? {key: 'strings:homePage.header.editProfile'}

        super(title, undefined, () => undefined, () => undefined, true, {showXonHeader: true})

        this.editUsernameAction = () => this.createUsernameController(editProfileData)
        this.selectColorAction = async(color: PlayerColor) => {
            this.selectedColor = color
            await editProfileData.selectColorAction(color)
            this.removePopup()
        }

        this.selectedColor = editProfileData.selectedColor
        this.removeFooter()

        this.createUsernameEditor(editProfileData.username)
        this.createTabToggle()
        this.createAvatarSelector()
        this.createColorSelector(editProfileData.selectedColor)

        this.setTab(editProfileData.tab ?? EditProfileTab.Avatar)

        this.contentContainer.classList.add('popup-edit-profile')
        this.body.classList.add('popup-edit-profile-body')
        this.show()
    }

    protected setTab(tab: EditProfileTab) {
        this.tab = tab

        if(this.tab == EditProfileTab.Avatar) {
            this.toggleButtonAvatar.classList.add('active')
            this.toggleButtonColor.classList.remove('active')
            this.avatarSelectorContainer.classList.remove('hidden')
            this.colorSelectorContainer.classList.add('hidden')
            return
        }

        this.toggleButtonColor.classList.add('active')
        this.toggleButtonAvatar.classList.remove('active')
        this.avatarSelectorContainer.classList.add('hidden')
        this.colorSelectorContainer.classList.remove('hidden')
    }

    private createUsernameController(editProfileData: EditProfileData) {
        const usernameControllerReturnsToEditor = new editProfileData.usernameController(undefined, undefined, () => new ViewPopupEditProfile({
            ...editProfileData,
            tab: this.tab,
            selectedColor: this.selectedColor,
        }))
        usernameControllerReturnsToEditor.show()
    }

    protected createUsernameEditor(username?: string) {
        const userIcon = ClientUserState.userState?.icon
        const userUsername = username ?? ClientUserState.userState?.username

        if(userIcon == undefined) return
        if(userUsername == undefined) return

        const userInformationContainer = createDivIn(this.body, 'edit-profile-user-information-container')
        const iconUsernameContainer = createDivIn(userInformationContainer, 'edit-profile-icon-username-container')
        createImgIn(iconUsernameContainer, userIcon, 'edit-profile-user-icon')
        createHeaderIn(iconUsernameContainer, {key: 'strings:colonistCommon.utils.#buffer', options: {value: userUsername}}, 'edit-profile-username', undefined, 'H2')

        createImgIn(userInformationContainer, LobbyImage.Pencil, 'edit-profile-edit-icon')

        setElementOnClick(userInformationContainer, () => {
            this.editUsernameAction()
            this.removePopup()
        })
    }

    private createTabToggle() {
        this.toggleContainer = createDivIn(this.body, 'toggle-container small-margins')

        this.toggleButtonAvatar = createDivIn(this.toggleContainer, 'toggle-tab')
        createParagraphIn(this.toggleButtonAvatar, {key: 'strings:storePage.sections.avatar.title', options: {context: 'one'}}, '')
        setElementOnClick(this.toggleButtonAvatar, () => this.setTab(EditProfileTab.Avatar))

        this.toggleButtonColor = createDivIn(this.toggleContainer, 'toggle-tab')
        createParagraphIn(this.toggleButtonColor, {key: 'strings:storePage.sections.color.title', options: {context: 'one'}}, '')
        setElementOnClick(this.toggleButtonColor, () => this.setTab(EditProfileTab.Color))
    }

    private createAvatarSelector() {
        this.avatarSelectorContainer = createDivIn(this.body, 'header-profile-selector-container hidden')
        const grid = createDivIn(this.avatarSelectorContainer, 'header-profile-selector-grid')
        new UIProfileEditAvatarController(this.avatarSelectorContainer, grid, () => this.removePopup())
    }

    private createColorSelector(selectedColor?: PlayerColor) {
        this.colorSelectorContainer = createDivIn(this.body, 'header-profile-selector-container hidden')
        const grid = createDivIn(this.colorSelectorContainer, 'header-profile-selector-grid')
        new UIPlayerColorController(this.colorSelectorContainer, grid, this.selectColorAction, selectedColor)
    }
}
